import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '@/lib/api-client'
import { CURRENT_AGREEMENT_VERSION } from '@/lib/agreement'

interface User {
  id: string
  email: string
  name: string | null
  image: string | null
  plan: string
  role: 'user' | 'admin' | 'superadmin'
  agreedToTermsAt?: string
  agreedToTermsVersion?: string
}

interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  isAdmin: boolean
  isSuperAdmin: boolean
  hasAcceptedCurrentTerms: boolean
  login: (email: string, password: string) => Promise<void>
  loginWithGitHub: (returnUrl?: string) => void
  loginWithGoogle: (returnUrl?: string) => void
  loginWithMicrosoft: (returnUrl?: string) => void
  loginWithSSO: (organization: string, returnUrl?: string) => void
  logout: () => Promise<void>
  refreshToken: () => Promise<void>
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)
const AUTH_USER_STORAGE_KEY = 'auth_user'

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()

  const clearSession = useCallback(() => {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem(AUTH_USER_STORAGE_KEY)
    apiClient.setAccessToken(null)
    setUser(null)
  }, [])

  const persistUser = useCallback((nextUser: User | null) => {
    if (!nextUser) {
      localStorage.removeItem(AUTH_USER_STORAGE_KEY)
      return
    }
    localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(nextUser))
  }, [])

  const redirectToLogin = useCallback((returnUrl?: string) => {
    const candidatePath = returnUrl || `${window.location.pathname}${window.location.search}${window.location.hash}` || '/overview'

    if (
      candidatePath.startsWith('/') &&
      !candidatePath.startsWith('//') &&
      !candidatePath.startsWith('/login') &&
      !candidatePath.startsWith('/auth/callback')
    ) {
      localStorage.setItem('auth_return_url', candidatePath)
      navigate(`/login?redirect=${encodeURIComponent(candidatePath)}`, { replace: true })
      return
    }

    navigate('/login', { replace: true })
  }, [navigate])

  const refreshTokenHandler = useCallback(async (refreshToken: string) => {
    try {
      const response = await apiClient.refreshToken(refreshToken)
      
      if (response.success && response.data) {
        const { access_token } = response.data
        localStorage.setItem('access_token', access_token)
        apiClient.setAccessToken(access_token)
        
        // Get user profile with new token
        const profileResponse = await apiClient.getProfile()
        if (profileResponse.success && profileResponse.data) {
          const nextUser = profileResponse.data as User
          setUser(nextUser)
          persistUser(nextUser)
        } else {
          throw new Error(profileResponse.error || 'Failed to load user profile')
        }
      } else {
        throw new Error(response.error || 'Failed to refresh token')
      }
    } catch (error) {
      console.error('Failed to refresh token:', error)
      const message = error instanceof Error ? error.message : String(error)
      const isAuthFailure = message.includes('401') || message.includes('403') || message.includes('400')
      if (isAuthFailure) {
        clearSession()
        redirectToLogin()
        return
      }
      throw error
    }
  }, [clearSession, persistUser, redirectToLogin])

  useEffect(() => {
    apiClient.setUnauthorizedHandler(() => {
      clearSession()
      redirectToLogin()
    })

    return () => {
      apiClient.setUnauthorizedHandler(null)
    }
  }, [clearSession, redirectToLogin])

  // Load user from localStorage on mount
  useEffect(() => {
    const loadUser = async () => {
      try {
        const accessToken = localStorage.getItem('access_token')
        const refreshToken = localStorage.getItem('refresh_token')
        const cachedUser = localStorage.getItem(AUTH_USER_STORAGE_KEY)
        if (cachedUser) {
          try {
            setUser(JSON.parse(cachedUser) as User)
          } catch {
            localStorage.removeItem(AUTH_USER_STORAGE_KEY)
          }
        }
        
        if (!accessToken) {
          setIsLoading(false)
          return
        }

        apiClient.setAccessToken(accessToken)
        const response = await apiClient.getProfile()
        
        if (response.success && response.data) {
          const nextUser = response.data as User
          setUser(nextUser)
          persistUser(nextUser)
        } else if (response.error?.includes('HTTP 401') && refreshToken) {
          // Try to refresh the token
          try {
            await refreshTokenHandler(refreshToken)
          } catch (refreshError) {
            console.error('Refresh failed during startup:', refreshError)
          }
        } else if (response.error?.includes('HTTP 401')) {
          clearSession()
        }
      } catch (error) {
        console.error('Failed to load user:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadUser()
  }, [clearSession, persistUser, refreshTokenHandler])

  const login = async (email: string, password: string) => {
    try {
      const response = await apiClient.login(email, password)
      
      if (response.success && response.data) {
        const { access_token, refresh_token, user } = response.data
        
        localStorage.setItem('access_token', access_token)
        localStorage.setItem('refresh_token', refresh_token)
        apiClient.setAccessToken(access_token)
        
        setUser(user)
        persistUser(user)
        navigate('/overview')
      } else {
        throw new Error(response.error || 'Login failed')
      }
    } catch (error) {
      console.error('Login error:', error)
      throw error
    }
  }

  const loginWithGitHub = (returnUrl?: string) => {
    const destination = returnUrl || `${window.location.pathname}${window.location.search}${window.location.hash}` || '/overview'
    if (destination.startsWith('/') && !destination.startsWith('//') && !destination.startsWith('/auth/callback')) {
      localStorage.setItem('auth_return_url', destination)
    }

    // Redirect to app-api GitHub OAuth endpoint (same origin)
    window.location.href = `${window.location.origin}/api/v1/auth/github`
  }

  const loginWithGoogle = (returnUrl?: string) => {
    const destination = returnUrl || `${window.location.pathname}${window.location.search}${window.location.hash}` || '/overview'
    if (destination.startsWith('/') && !destination.startsWith('//') && !destination.startsWith('/auth/callback')) {
      localStorage.setItem('auth_return_url', destination)
    }

    window.location.href = `${window.location.origin}/api/v1/auth/google`
  }

  const loginWithMicrosoft = (returnUrl?: string) => {
    const destination = returnUrl || `${window.location.pathname}${window.location.search}${window.location.hash}` || '/overview'
    if (destination.startsWith('/') && !destination.startsWith('//') && !destination.startsWith('/auth/callback')) {
      localStorage.setItem('auth_return_url', destination)
    }

    window.location.href = `${window.location.origin}/api/v1/auth/microsoft`
  }

  const loginWithSSO = (organization: string, returnUrl?: string) => {
    const destination = returnUrl || `${window.location.pathname}${window.location.search}${window.location.hash}` || '/overview'
    if (destination.startsWith('/') && !destination.startsWith('//') && !destination.startsWith('/auth/callback')) {
      localStorage.setItem('auth_return_url', destination)
    }

    // Redirect to app-api SSO endpoint (same origin)
    window.location.href = `${window.location.origin}/api/v1/auth/sso?organization=${encodeURIComponent(organization)}`
  }

  const logout = async () => {
    try {
      await apiClient.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      clearSession()
      navigate('/login')
    }
  }

  const refreshToken = useCallback(async () => {
    const refreshTokenValue = localStorage.getItem('refresh_token')
    if (refreshTokenValue) {
      await refreshTokenHandler(refreshTokenValue)
    }
  }, [refreshTokenHandler])

  const hasAcceptedCurrentTerms = user?.agreedToTermsVersion === CURRENT_AGREEMENT_VERSION

  const value = {
    user,
    isLoading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin' || user?.role === 'superadmin',
    isSuperAdmin: user?.role === 'superadmin',
    hasAcceptedCurrentTerms,
    login,
    loginWithGitHub,
    loginWithGoogle,
    loginWithMicrosoft,
    loginWithSSO,
    logout,
    refreshToken,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
